/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material'
import {
  ContentType,
  contentTypes,
  FileData,
} from 'src/v3/utilities/filesystem'
import { CloseIcon, CloudUploadIcon, EditIcon } from 'src/v3/core/icons'

import { PinturaEditor } from '@pqina/react-pintura'
import { getEditorDefaults } from '@pqina/pintura'

import '@pqina/pintura/pintura.css'
import axios from 'axios'
import { useCdnImageRetry } from 'src/v3/hooks/use-cdn-image-retry'

/**
 * Fetch image as blob via axios (handles CORS via backend proxy if configured)
 */
function fetchImageAsBlob(imageUrl: string): Promise<Blob> {
  return axios
    .get(imageUrl, { responseType: 'blob' })
    .then((response) => response.data)
    .catch(() => Promise.reject(new Error('Failed to load image')))
}

let ImageUpload = ({
  id,
  type,
  image,
  setImage,
  existingImage,
  imageBasePath = 'images',
  sxOverride = {},
}: {
  id: string
  type: 'banner' | 'thumbnail' | 'landscape' | '2/3' | 'default'
  image: File | undefined
  setImage: React.Dispatch<React.SetStateAction<File | undefined>>
  existingImage?: string
  imageBasePath?: 'images' | 'public'
  sxOverride?: object
}): ReactElement => {
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>()
  let [imgCropModal, setImgCropModal] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [loadingExistingImage, setLoadingExistingImage] = useState<boolean>(false)

  let cdnUrl =
    !image && existingImage
      ? imageBasePath === 'public'
        ? `${import.meta.env.VITE_CLOUDFRONT_URL}/public/${existingImage}`
        : `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${existingImage?.replace('originals', 'resized')}`
      : null

  let {
    src: cdnSrc,
    onError: cdnOnError,
    isRetrying,
    hasError: imgError,
  } = useCdnImageRetry(cdnUrl)

  let handleCapture = (event: React.ChangeEvent) => {
    let file = (event.target as HTMLInputElement).files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        setErrorMessage('Image upload failed: read file failed')
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        setErrorMessage(`Invalid file type: ${fileType}`)
        return
      }

      let base64 = reader.result?.toString().split(',')[1] as string
      let binaryData = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
      let blobData = new Blob([binaryData], { type: fileType })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
      setImage(undefined)
      setImgCropModal(true)
    }

    if (file) {
      reader.readAsDataURL(file)
    } else {
      setErrorMessage('No file selected')
    }
  }

  let dropHandler = (event: React.DragEvent) => {
    event.preventDefault()

    if (event.dataTransfer.items) {
      let items = [...event.dataTransfer.items]

      items.forEach((item) => {
        if (item.kind === 'file') {
          let file = item.getAsFile()
          if (file) {
            let fileData: FileData = {
              name: file.name,
              type: file.type,
              extension: file.type.split('/')[1],
              file,
            }

            if (['png', 'jpg', 'jpeg'].includes(fileData.extension)) {
              setFileToUpload(fileData)
              setImage(undefined)
              setImgCropModal(true)
            } else {
              setErrorMessage(`Invalid file type: ${fileData.extension}`)
            }
          }
        }
      })
    } else {
      setErrorMessage('File upload failed')
    }
  }

  let name = `${id}-image`
  let aspectRatio: Record<typeof type, number | undefined> = {
    thumbnail: 16 / 9,
    banner: 4 / 1,
    landscape: 11 / 8.5,
    '2/3': 2 / 3,
    default: undefined,
  }

  return (
    <div
      onDrop={dropHandler}
      onDragOver={(ev: React.DragEvent) => ev.preventDefault()}
      role="button"
      tabIndex={0}
      aria-label={`Upload ${type} image by dragging and dropping or browsing files`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          document.getElementById(name)?.click()
        }
      }}
    >
      <input
        type="file"
        accept=".png,.jpg,.jpeg"
        id={name}
        style={{ display: 'none' }}
        onChange={(e) => handleCapture(e)}
      />
      <Box
        sx={{
          borderWidth: 2,
          borderStyle: 'dashed',
          borderColor: 'background.paper',
          borderRadius: 2,
          bgcolor: '#0b0b0b',
          px: 2,
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          ...sxOverride,
        }}
      >
        <CloudUploadIcon sx={{ color: 'grey', height: 40, width: 60 }} />
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ mt: 1, mb: 0.5 }}
        >
          Drag and drop your image here, or
        </Typography>
        <label style={{ marginTop: 0 }} htmlFor={name}>
          <Button
            component="span"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            browse files
          </Button>
        </label>
        <Typography sx={{ color: '#787878' }} variant="subtitle2">
          Maximum file size: 5MB
        </Typography>
      </Box>
      {existingImage &&
        !image &&
        !imgCropModal &&
        (isRetrying || (imgError && imageBasePath === 'public')) && (
          <Card
            variant="outlined"
            sx={{
              mt: 1,
              p: 2,
              pr: 3,
              bgcolor: 'background.paperDark',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {imgError ? 'Image unavailable' : 'Processing image…'}
            </Typography>
          </Card>
        )}
      {(existingImage || image) &&
        !imgCropModal &&
        !(isRetrying && !image) &&
        !(imgError && !image && imageBasePath === 'public') && (
          <Card
            variant="outlined"
            sx={{
              mt: 1,
              p: 2,
              pr: 3,
              bgcolor: 'background.paperDark',
              display: 'grid',
              gridTemplateColumns: '1fr 40px',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 3,
              }}
            >
              <img
                style={{ height: 80, border: '1px solid #6d6d6d' }}
                src={
                  image
                    ? URL.createObjectURL(image)
                    : imgError && imageBasePath === 'images'
                      ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${existingImage}`
                      : (cdnSrc ?? undefined)
                }
                onError={image || imgError ? undefined : cdnOnError}
              />
              {fileToUpload && (
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    {fileToUpload?.name}
                  </Typography>
                  {image && (
                    <Typography variant="subtitle2" color="textSecondary">
                      {Math.round(image.size / 10000) / 100}MB
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <IconButton
              disabled={loadingExistingImage}
              onClick={() => {
                if (!fileToUpload) {
                  setLoadingExistingImage(true)

                  // Use S3 URL for editing (CORS-enabled) instead of CloudFront
                  // S3 bucket allows CORS, while CloudFront does not
                  let s3BaseUrl =
                    'https://foamfrat-public.s3.us-east-2.amazonaws.com'
                  let imageUrl =
                    imageBasePath === 'public'
                      ? `${s3BaseUrl}/public/${existingImage}`
                      : `${s3BaseUrl}/images/${existingImage?.replace('originals', 'resized')}`

                  // Fetch image as blob from S3 (should have CORS configured)
                  fetchImageAsBlob(imageUrl)
                    .then((blob) => {
                      // Detect file extension from existingImage path
                      let parts = existingImage?.split('.') || []
                      let extension = parts[parts.length - 1] || 'jpg'
                      let mimeType =
                        extension === 'png' ? 'image/png' : 'image/jpeg'

                      setFileToUpload({
                        name: `${type}.${extension}`,
                        extension,
                        type: mimeType,
                        file: blob,
                      })
                      setImgCropModal(true)
                    })
                    .catch((err: any) => {
                      setErrorMessage(
                        err?.message ||
                          `Error loading ${type} image for editing`,
                      )
                    })
                    .finally(() => {
                      setLoadingExistingImage(false)
                    })
                } else {
                  setImgCropModal(true)
                }
              }}
            >
              <EditIcon />
            </IconButton>
          </Card>
        )}
      <Dialog open={imgCropModal && !!fileToUpload} fullWidth>
        <Card variant="outlined" sx={{ px: 2, py: 3, overflow: 'scroll' }}>
          <IconButton
            sx={{ position: 'absolute', right: 16, top: 16 }}
            onClick={() => setImgCropModal(false)}
            aria-label="Close image editor"
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              mb: type === 'default' ? 1.5 : 0,
            }}
          >
            Crop Image
          </Typography>
          {type !== 'default' && (
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{
                textAlign: 'center',
                mb: 0.5,
                mt: 1,
              }}
            >
              All {type} images must have a {type === 'banner' ? '4:1' : '16:9'}{' '}
              aspect ratio. Drag to reposition, use handles to crop.
            </Typography>
          )}
          <div style={{ height: 500 }}>
            <PinturaEditor
              {...getEditorDefaults()}
              src={image || fileToUpload?.file || undefined}
              onProcess={(res) => {
                setImage(res.dest)
                setImgCropModal(false)
              }}
              imageCropAspectRatio={aspectRatio[type]}
            />
          </div>
        </Card>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default ImageUpload
